import type { ComponentChildren } from 'preact';
import { Cover, Pill, ReadTypeLabel, type ReadType } from './bits';

// How a book shows in a shelf or list. Anywhere a book appears, its cover and title open it
// (decisions doc › Shared pattern, Oct 20); buttons on a card keep their own jobs.

export interface BookSummary {
  id: string;
  title: string;
  author: string;
  series?: string;
  coverUrl?: string | null;
  readType?: ReadType | null;
  genre?: string;
  /** Covers: one subgenre under the author (Kristi, Oct 28). */
  subgenre?: string;
  /** A library copy is involved (borrowed or on hold). */
  library?: boolean;
}

/**
 * Book tile (covers view): the read-type value bar above the cover, the cover, title,
 * author, and one quiet line (progress, when paused, a short library pill…).
 */
export function BookTile({
  book,
  line,
  libraryPill,
  notOwned,
  onOpen,
}: {
  book: BookSummary;
  line?: string;
  libraryPill?: string;
  /** "Not owned yet" in a small dashed pill (Kristi, Oct 28: as in More like this). */
  notOwned?: boolean;
  onOpen: () => void;
}) {
  return (
    <div class="tile">
      <span class={`tile-bar rt-bar-${book.readType ?? 'none'}`} aria-hidden="true" />
      <button type="button" class="tile-open" onClick={onOpen} aria-label={`Open ${book.title}`}>
        <Cover title={book.title} url={book.coverUrl} size="md" />
      </button>
      <button type="button" class="tile-title" onClick={onOpen} tabIndex={-1} aria-hidden="true">
        {book.title}
      </button>
      <span class="tile-author">{book.author}</span>
      {book.subgenre ? <span class="tile-subgenre">{book.subgenre}</span> : null}
      {notOwned ? <span class="tile-notowned">Not owned yet</span> : null}
      {libraryPill ? <Pill>{libraryPill}</Pill> : null}
      {line ? <span class="tile-line">{line}</span> : null}
    </div>
  );
}

/**
 * Book card (Currently Reading style, decisions doc › Currently Reading card): read-type
 * label in the top right corner; for a library copy, a narrow T7 bar on the left edge with
 * "Library copy" running up beside it; the cover with the genre as plain text under it
 * (style guide, Kristi Oct 26); title, series · book, author; at most two pills; one quiet
 * line; a progress bar; a two-line note; the action row, ending with Move ▾.
 */
export function BookCard({
  book,
  pills,
  notOwned,
  line,
  progress,
  note,
  actions,
  faded,
  onOpen,
}: {
  book: BookSummary;
  pills?: ComponentChildren;
  /** "Not owned yet" in a small dashed pill after the pills (Kristi, Oct 28). */
  notOwned?: boolean;
  /** One quiet line, or several (Shelves: "Finished Mar 2024" on its own line). */
  line?: string | string[];
  /** 0–100 when reading. */
  progress?: number;
  note?: string;
  actions?: ComponentChildren;
  faded?: boolean;
  onOpen: () => void;
}) {
  return (
    <article
      class={`card rt-edge-${book.readType ?? 'none'}${book.library ? ' library' : ''}${faded ? ' faded' : ''}`}
      aria-label={book.title}
    >
      {book.library ? (
        <span class="library-label" aria-hidden="true">
          <span>Library copy</span>
        </span>
      ) : null}
      <span class="card-corner">
        <ReadTypeLabel type={book.readType} />
      </span>
      <div class="card-left">
        <button type="button" class="card-open" onClick={onOpen} aria-label={`Open ${book.title}`}>
          <Cover title={book.title} url={book.coverUrl} size="md" />
        </button>
        {book.genre ? (
          <span class="card-genre" title={book.genre}>
            {book.genre}
          </span>
        ) : null}
      </div>
      <div class="card-main">
        <button type="button" class="card-title" onClick={onOpen}>
          {book.title}
        </button>
        {book.series ? <span class="card-series">{book.series}</span> : null}
        <span class="card-author">{book.author}</span>
        {book.library ? <span class="visually-hidden">Library copy</span> : null}
        {pills || notOwned ? (
          <span class="card-pills">
            {pills}
            {notOwned ? <span class="tile-notowned card-notowned">Not owned yet</span> : null}
          </span>
        ) : null}
        {(Array.isArray(line) ? line : line ? [line] : []).filter(Boolean).map((l) => (
          <span key={l} class="card-line">
            {l}
          </span>
        ))}
        {progress !== undefined ? (
          <span class="progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Progress">
            <span style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
          </span>
        ) : null}
        {note ? <p class="card-note">{note}</p> : null}
        {actions ? <div class="card-actions">{actions}</div> : null}
      </div>
    </article>
  );
}
